<template>
    <popup v-model="isActive" :persistent="persistent || !!cancelCheck" :highest-order="highestOrder" :order="order" @closed="handleCanceled">
        <card :width="modalWidth" :height="modalHeight" :max-width="maxModalWidth" :max-height="maxModalHeight" no-spacing
              v-model:loading="isLoading" :tile="isFullscreen" :rounded-x-large="!isFullscreen">
            <div v-if="title || $slots.title" class="flex justify-between py-16 px-16 md:py-20 lg:px-24 border-b">
                <div class="flex flex-column truncate flex-grow-x-1">
                    <slot name="title"/>
                    <p v-if="title" class="text-md font-bold text-heading-color truncate mb-0">{{ title }}</p>
                    <p v-if="subtitle" class="text-xs text-subtext-color leading-normal mb-0 mt-2 whitespace-normal">{{ subtitle }}</p>
                </div>
                <div v-if="closeable" class="ml-16">
                    <Icon :icon="icons.close" class="text-gray-400 hover:text-gray-500 cursor-pointer" @click="cancel"/>
                </div>
            </div>
            <slot name="header"/>
            <card-text class="border-box overflow-y-auto" fill :class="[contentClass, {'p-0': noPadding}]">
                <slot/>
            </card-text>
            <slot name="footer"/>
            <wrapper v-if="(showCloseButton && closeable) || $slots.button" class="px-16 py-16 lg:px-24 border-box border-t" :grow="!!$slots.button" between
                     :break-column="buttonBreakColumn" :class="{'mt-0': noPadding}">
                <btn v-if="showCloseButton && closeable" text @click="cancel" class="max-w-120">{{ $t('action.close') }}</btn>
                <slot name="button"/>
            </wrapper>
        </card>
    </popup>
</template>

<script>
import Sizeable from "../../mixins/Sizeable";
import Loadable from "../../mixins/Loadable";
import Toggleable from "../../mixins/Toggleable";
import {mdiClose} from "@mdi/js";
import {closeIcon} from "../../config";

export default {
    name: 'Modal',

    mixins: [Toggleable, Sizeable, Loadable],

	emits: ['show', 'hide', 'cancel'],

    props: {

        // Whether the modal shall be put as highest z-index position if multiple modals and popovers are displayed at the same time
        highestOrder: {
            type: Boolean,
            default: false
        },

        // The order number of the modal if multiple modals are displayed at the same time
        // Is equal to a CSS 'z-index'
        order: {
            type: Number,
            default: null
        },

        title: {
            type: String,
            default: null
        },

        subtitle: {
            type: String,
            default: null
        },

        contentClass: {
            type: [String , Object ,  Array],
            default: ''
        },

        showCloseButton: {
            type: Boolean,
            default: true
        },

        closeable: {
            type: Boolean,
            default: true
        },

        persistent: {
            type: Boolean,
            default: false
        },

        // Whether the modal content shall no have any content
        noPadding: {
            type: Boolean,
            default: false
        },

        // A promise function that returns a boolean to check if the modal can be canceled.
        cancelCheck: {
            type: Function,
            default: null
        },

        fullscreen: {
            type: Boolean,
            default: false
        },

        mobileFullscreen: {
            type: Boolean,
            default: true
        },

        width: {
            type: [Number, String],
            default: null
        },

        buttonBreakColumn: {
            type: String,
            default: null
        }
    },

	data() {
		return {
			icons: {
				close: closeIcon,
			}
		}
	},

    computed: {

        isFullscreen() {
            return this.fullscreen || (this.mobileFullscreen && this.$device.isMobile);
        },

        modalWidth() {
            if (this.isFullscreen) {
                return '100vw';
            }

            if (this.width) {
                return this.width;
            }

            switch (this.sizeString) {
                case 'xl':
                    return '1200';
                case 'lg':
                    return '900';
                case 'sm':
                    return '500';
            }

            return '650';
        },

        modalHeight() {
            if (this.isFullscreen) {
				return '100%';
            }

            return null;
        },

        maxModalWidth() {
            if (this.isFullscreen) {
                return '100%';
            }

            return '90vw';
        },

		maxModalHeight() {
			if (this.isFullscreen) {
				return '100%';
			}

			return '90vh';
		}
    },

    watch: {
        isActive(active) {
            if (active) {
                this.$emit('show');
            } else {
                this.$emit('hide');
            }
        }
    },

    methods: {
        cancel() {
            if (this.cancelCheck) {
                this.cancelCheck().then(({confirmed}) => {
                    if (confirmed) {
                        this.cancelNow();
                    }
                })
            } else {
                this.cancelNow();
            }
        },

        cancelNow() {
            this.isActive = false;
            this.handleCanceled();
        },

        handleCanceled() {
            this.$emit('cancel');
        }

    }
}
</script>